import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Seo from '../components/Seo'
import { getCmsCollection, getCmsPage } from '../lib/cms'

const DEFAULT_TASKS = [
  {
    title: 'Nedgravning af trampolin',
    description: 'En nedgravet trampolin giver et flot og sikkert udtryk i haven.',
    price: 'Ca. 4.000 – 8.000 kr.',
    note: 'inkl. bortkørsel af jord.',
    icon: 'fa-solid fa-circle-notch',
    order: 1,
  },
  {
    title: 'Opgravning af hæk',
    description: 'Skal hækken væk for at give plads til noget nyt? Det kræver både opgravning og bortskaffelse af rødder.',
    price: 'Ca. 2.500 – 6.000 kr.',
    note: 'afhængig af længde og hæktype.',
    icon: 'fa-solid fa-tree',
    order: 2,
  },
  {
    title: 'Nedgravning af kabler',
    description: 'Typisk i forbindelse med el, internet eller belysning i haven.',
    price: 'Ca. 300 – 500 kr. pr. løbende meter',
    note: 'inkl. materialer.',
    icon: 'fa-solid fa-bolt',
    order: 3,
  },
  {
    title: 'Nedgravning af samletank',
    description: 'En samletank anvendes ofte i områder uden kloaktilslutning.',
    price: 'Ca. 20.000 – 35.000 kr.',
    note: 'alt inkl. (gravning, levering og tilslutning).',
    icon: 'fa-solid fa-oil-can',
    order: 4,
  },
  {
    title: 'Nedgravning af faskine',
    description: 'Faskiner bruges til at aflede regnvand fra tag eller terrasse.',
    price: 'Ca. 5.000 – 10.000 kr.',
    note: 'inkl. materialer og arbejdsløn.',
    icon: 'fa-solid fa-cloud-rain',
    order: 5,
  },
  {
    title: 'Planering og afretning af have eller grund',
    description: 'Planering bruges til at udjævne terræn før græs, belægning eller ny beplantning.',
    price: 'Ca. 5.000 – 12.000 kr.',
    note: 'afhængigt af grundens størrelse og mængden af jord, der skal flyttes.',
    icon: 'fa-solid fa-ruler-combined',
    order: 6,
  },
  {
    title: 'Udgravning til fundament',
    description: 'Ved nybyggeri eller tilbygning er udgravning til fundament en nødvendighed.',
    price: 'Ca. 15.000 – 30.000 kr.',
    note: 'afhængigt af størrelse og jordforhold.',
    icon: 'fa-solid fa-building-columns',
    order: 7,
  },
  {
    title: 'Nedgravning af jordvarmeslanger',
    description: 'Et jordvarmeanlæg kræver udgravning af lange render i haven.',
    price: 'Ca. 40.000 – 80.000 kr.',
    note: 'alt efter grundens størrelse og anlæggets behov.',
    icon: 'fa-solid fa-temperature-arrow-up',
    order: 8,
  },
  {
    title: 'Nedgravning af omfangsdræn',
    description: 'Et omfangsdræn hjælper med at holde fugt væk fra huset og kræver præcist og dybt gravearbejde.',
    price: 'Ca. 2.000 – 3.500 kr. pr. løbende meter',
    note: 'inkl. drænrør og tilslutning.',
    icon: 'fa-solid fa-droplet-slash',
    order: 9,
  },
  {
    title: 'Nedgravning af septiktank',
    description: 'En septiktank kræver en stor udgravning og korrekt placering.',
    price: 'Ca. 25.000 – 45.000 kr.',
    note: 'afhængig af størrelse og jordtype.',
    icon: 'fa-solid fa-filter',
    order: 10,
  },
  {
    title: 'Udgravning til indkørsel eller terrasse',
    description: 'Ved etablering af ny indkørsel, flisebelægning eller terrasse er korrekt udgravning afgørende for et stabilt underlag.',
    price: 'Ca. 8.000 – 18.000 kr.',
    note: 'afhængigt af areal, dybde og jordbundsforhold.',
    icon: 'fa-solid fa-road',
    order: 11,
  },
]

const DEFAULT_STANDARD_ITEMS = [
  {
    title: 'Maskine fører',
    price: '325kr',
    description: 'Værdi er en subjektiv opfattelse af betydningen eller nytten af noget, som varierer fra person til person.',
    icon: 'fa-solid fa-user-gear',
    order: 12,
  },
  {
    title: 'Minigravere',
    price: '270kr',
    description: 'Værdi er en subjektiv opfattelse af betydningen eller nytten af noget, som varierer fra person til person.',
    icon: 'fa-solid fa-truck-pickup',
    order: 13,
  },
  {
    title: 'Bulldozere',
    price: '1250kr',
    description: 'Værdi er en subjektiv opfattelse af noget, der anses som vigtigt, nyttigt eller ønskeligt af en person eller samfund.',
    icon: 'fa-solid fa-tractor',
    order: 14,
  },
  {
    title: 'Dumpere',
    price: '3600kr',
    description: 'Værdi kan være en moralsk eller økonomisk størrelse, der repræsenterer betydningen eller nytten af noget.',
    icon: 'fa-solid fa-truck-ramp-box',
    order: 15,
  },
]

const DEFAULT_SPECIALIZED_ITEMS = [
  {
    title: 'Gravemaskiner',
    price: '250-3500kr',
    description: 'Værdi er en subjektiv og individuel opfattelse af noget, der betyder noget for os.',
    icon: 'fa-solid fa-truck-monster',
    order: 16,
  },
  {
    title: 'Rendegravere',
    price: '2400kr',
    description: 'Værdi er en subjektiv opfattelse af betydningen og nytten af noget.',
    icon: 'fa-solid fa-dolly',
    order: 17,
  },
  {
    title: 'Skræntklippere',
    price: '3600kr',
    description: 'Værdi er en subjektiv opfattelse af, hvor vigtig eller meningsfuld noget er for en person eller en gruppe.',
    icon: 'fa-solid fa-scissors',
    order: 18,
  },
]

export default function PricingPage() {
  const [items, setItems] = useState([])
  const [pageData, setPageData] = useState(null)

  useEffect(() => {
    async function loadData() {
      try {
        const [prices, page] = await Promise.all([
          getCmsCollection('priser'),
          getCmsPage('prisliste'),
        ])
        if (Array.isArray(prices) && prices.length > 0) {
          setItems(prices)
        }
        if (page) setPageData(page)
      } catch (err) {
        console.error('Error loading pricing data:', err)
      }
    }
    loadData()
  }, [])

  const tasks = useMemo(() => {
    if (!items || items.length === 0) return DEFAULT_TASKS
    const filtered = items.filter((item) => {
      const termList = item.terms?.priskategori || []
      return (
        termList.includes('gravearbejde-opgaver') ||
        item.category === 'Gravearbejde opgaver' ||
        item.category === 'gravearbejde-opgaver' ||
        (item.order >= 1 && item.order <= 11)
      )
    })
    return filtered.length > 0
      ? filtered.sort((a, b) => (a.order || 0) - (b.order || 0))
      : DEFAULT_TASKS
  }, [items])

  const standardItems = useMemo(() => {
    if (!items || items.length === 0) return DEFAULT_STANDARD_ITEMS
    const filtered = items.filter((item) => {
      const termList = item.terms?.priskategori || []
      return (
        termList.includes('standard-jordarbejde') ||
        item.category === 'Standard jordarbejde' ||
        item.category === 'standard-jordarbejde' ||
        (item.order >= 12 && item.order <= 15)
      )
    })
    return filtered.length > 0
      ? filtered.sort((a, b) => (a.order || 0) - (b.order || 0))
      : DEFAULT_STANDARD_ITEMS
  }, [items])

  const specializedItems = useMemo(() => {
    if (!items || items.length === 0) return DEFAULT_SPECIALIZED_ITEMS
    const filtered = items.filter((item) => {
      const termList = item.terms?.priskategori || []
      return (
        termList.includes('specialiserede-jordarbejder') ||
        item.category === 'Specialiserede jordarbejder' ||
        item.category === 'specialiserede-jordarbejder' ||
        (item.order >= 16 && item.order <= 18)
      )
    })
    return filtered.length > 0
      ? filtered.sort((a, b) => (a.order || 0) - (b.order || 0))
      : DEFAULT_SPECIALIZED_ITEMS
  }, [items])

  const heroTitle = pageData?.hero_title || 'Hvad er prisen på forskelligt gravearbejde?'
  const heroDescription = pageData?.hero_description || 'Som entreprenør er det vigtigt for os at være gennemsigtige og fair over for vores kunder. Derfor har vi udarbejdet en prisliste, der tydeligt viser vores priser og ydelser. Vi ønsker, at vores kunder føler sig trygge og velinformerede, når de samarbejder med os.'

  return (
    <>
      <Seo
        title="Prisliste :: Lyn Entreprenør"
        description="Hvad er prisen på forskelligt gravearbejde? Som entreprenør er det vigtigt for os at være gennemsigtige og fair over for vores kunder. Derfor har vi udarbejdet en prisliste."
        ogImage="https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/fulllogo-transparent-nobuffer-2_ebf7d56d.png"
        canonicalUrl="https://www.lyn-ent.dk/prisliste/"
      />

      {/* Page Hero */}
      <section className="bg-red-600 py-16 sm:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-left">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight max-w-4xl">
            {heroTitle}
          </h1>

          <p className="mt-4 text-base sm:text-lg text-red-100 max-w-3xl leading-relaxed">
            {heroDescription}
          </p>
        </div>

        {/* V-shaped divider at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg
            className="relative block w-full h-8 sm:h-12 md:h-16 lg:h-20 text-slate-50"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
          </svg>
        </div>
      </section>

      {/* Specific Gravearbejde Opgaver Table */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-8 max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600">
              Prisoverslag
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
              Gravearbejde opgaver
            </h2>
            <p className="mt-2 text-sm sm:text-base text-slate-600">
              Oversigt over vejledende priser på hyppigt udførte opgaver for private og erhverv.
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {/* Mobilvisning (< sm): Rene, overskuelige kort med fast hierarki */}
            <div className="sm:hidden divide-y divide-slate-100">
              {tasks.map((task, idx) => (
                <div key={idx} className="p-4 bg-white hover:bg-slate-50/60 transition-colors text-left">
                  <h3 className="font-bold text-slate-900 text-sm leading-snug">
                    {task.title}
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed mt-1">
                    {task.description}
                  </p>
                  <div className="mt-2 text-left">
                    <span className="text-xs font-semibold text-slate-500 block">
                      Vejledende pris
                    </span>
                    <span className="font-extrabold text-red-600 text-sm block mt-0.5">
                      {task.price}
                    </span>
                    {task.note && (
                      <span className="text-[11px] text-slate-400 font-medium block mt-0.5">
                        {task.note}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop & Tablet visning (>= sm) */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left border-collapse table-fixed">
                <thead>
                  <tr className="bg-slate-100/90 text-slate-700 border-b border-slate-200 text-xs font-bold uppercase tracking-wider">
                    <th scope="col" className="py-4 px-4 sm:px-6 w-2/5 md:w-1/3">Ydelse / Opgave</th>
                    <th scope="col" className="py-4 px-4 sm:px-6 hidden md:table-cell md:w-5/12">Beskrivelse</th>
                    <th scope="col" className="py-4 px-4 sm:px-6 text-right w-3/5 md:w-1/4">Vejledende pris</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {tasks.map((task, idx) => (
                    <tr key={idx} className="hover:bg-red-50/30 transition-colors odd:bg-white even:bg-slate-50/40">
                      <td className="py-4 px-4 sm:px-6 align-top">
                        <span className="font-bold text-slate-900 block text-sm sm:text-base">
                          {task.title}
                        </span>
                        <p className="md:hidden text-xs text-slate-500 mt-1 leading-relaxed">
                          {task.description}
                        </p>
                      </td>
                      <td className="py-4 px-4 sm:px-6 text-slate-600 text-sm leading-relaxed align-top hidden md:table-cell">
                        {task.description}
                      </td>
                      <td className="py-4 px-4 sm:px-6 align-top text-right">
                        <span className="font-extrabold text-red-600 text-sm sm:text-base block">
                          {task.price}
                        </span>
                        {task.note && (
                          <span className="text-xs text-slate-500 font-medium block mt-0.5">
                            {task.note}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Standard jordarbejde Section */}
      <section className="py-16 sm:py-20 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-8 max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600">
              Udstyr & Førere
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
              Standard jordarbejde
            </h2>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 text-slate-700 border-b border-slate-200 text-xs font-bold uppercase tracking-wider">
                    <th scope="col" className="py-4 px-4 sm:px-6">Maskine / Ressource</th>
                    <th scope="col" className="py-4 px-4 sm:px-6 hidden md:table-cell">Beskrivelse</th>
                    <th scope="col" className="py-4 px-4 sm:px-6 text-right whitespace-nowrap">Pris</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {standardItems.map((item, idx) => (
                    <tr key={idx} className="hover:bg-red-50/30 transition-colors odd:bg-white even:bg-slate-50/40">
                      <td className="py-4 px-4 sm:px-6 align-top">
                        <span className="font-bold text-slate-900 block text-sm sm:text-base">
                          {item.title}
                        </span>
                        <p className="md:hidden text-xs text-slate-500 mt-1 leading-relaxed">
                          {item.description}
                        </p>
                      </td>
                      <td className="py-4 px-4 sm:px-6 text-slate-600 text-sm leading-relaxed align-top hidden md:table-cell">
                        {item.description}
                      </td>
                      <td className="py-4 px-4 sm:px-6 align-top text-right whitespace-nowrap">
                        <span className="font-extrabold text-red-600 text-sm sm:text-base block">
                          {item.price}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Specialiserede jordarbejder Section */}
      <section className="py-16 sm:py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-8 max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600">
              Specialopgaver
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
              Specialiserede jordarbejder
            </h2>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 text-slate-700 border-b border-slate-200 text-xs font-bold uppercase tracking-wider">
                    <th scope="col" className="py-4 px-4 sm:px-6">Maskine / Opgave</th>
                    <th scope="col" className="py-4 px-4 sm:px-6 hidden md:table-cell">Beskrivelse</th>
                    <th scope="col" className="py-4 px-4 sm:px-6 text-right whitespace-nowrap">Pris</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {specializedItems.map((item, idx) => (
                    <tr key={idx} className="hover:bg-red-50/30 transition-colors odd:bg-white even:bg-slate-50/40">
                      <td className="py-4 px-4 sm:px-6 align-top">
                        <span className="font-bold text-slate-900 block text-sm sm:text-base">
                          {item.title}
                        </span>
                        <p className="md:hidden text-xs text-slate-500 mt-1 leading-relaxed">
                          {item.description}
                        </p>
                      </td>
                      <td className="py-4 px-4 sm:px-6 text-slate-600 text-sm leading-relaxed align-top hidden md:table-cell">
                        {item.description}
                      </td>
                      <td className="py-4 px-4 sm:px-6 align-top text-right whitespace-nowrap">
                        <span className="font-extrabold text-red-600 text-sm sm:text-base block">
                          {item.price}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Guideline Banner: Hvad koster gravearbejde? */}
      <section className="py-16 sm:py-20 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 p-8 sm:p-10 text-white shadow-xl flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="space-y-3 max-w-3xl">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
                Hvad koster gravearbejde?
              </h2>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                Prisen på gravearbejde afhænger af flere faktorer, men som tommelfingerregel ligger timeprisen for en gravemaskine med fører typisk mellem <strong className="text-white">600 og 1.000 kr. i timen</strong>. For mindre opgaver kan der være en fast pris, mens større projekter ofte afregnes pr. time eller efter tilbud.
              </p>
            </div>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-6 py-4 text-sm font-bold text-white hover:bg-red-700 transition whitespace-nowrap active:scale-95 self-start lg:self-center"
            >
              <span>Få et uforpligtende tilbud</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
